<template>
  <div class="mc-site mc-home">
    <AppNavigation />

    <!-- ==================== HERO ==================== -->
    <section class="hero">
      <div class="hero-ledger" aria-hidden="true"></div>

      <div class="mc-container hero-inner">
        <div class="hero-copy">
          <div class="mc-kicker" data-reveal>For city &amp; town halls</div>

          <h1 class="hero-title" data-reveal="1">
            Every dollar your city is owed, <span class="mc-mark">collected</span> in one place.
          </h1>

          <p class="hero-sub" data-reveal="2">
            Water and sewer, property tax, excise, parking tickets, permits and records requests.
            Residents pay in one app, officers write tickets on one handheld, and City Hall works
            from one dashboard.
          </p>

          <div class="hero-ctas" data-reveal="3">
            <router-link to="/demo" class="mc-btn mc-btn-primary">
              Request a Demo
              <SiteIcon name="arrowRight" :size="18" />
            </router-link>
            <a href="#pricing" class="mc-btn mc-btn-outline" @click.prevent="scrollTo('pricing')">See pricing</a>
          </div>

          <dl class="hero-figures" data-reveal="4">
            <div v-for="f in heroFigures" :key="f.label" class="figure">
              <dt>{{ f.label }}</dt>
              <dd>{{ f.value }}</dd>
            </div>
          </dl>
        </div>

        <!-- Composition: a parking slip stamped PAID behind the resident app -->
        <div class="hero-visual" data-reveal="2" aria-hidden="true">
          <div class="slip">
            <div class="slip-perf"></div>
            <div class="slip-head">
              <div class="slip-kicker">Parking violation notice</div>
              <div class="slip-no">No. 100482</div>
            </div>
            <div class="slip-rows">
              <div v-for="r in slipRows" :key="r[0]" class="slip-row">
                <span>{{ r[0] }}</span><i></i><b>{{ r[1] }}</b>
              </div>
            </div>
            <div class="slip-qr">
              <span v-for="n in 49" :key="n" :class="{ on: qrOn(n) }"></span>
            </div>
            <div class="slip-foot">Scan to pay or contest</div>
            <div class="mc-stamp slip-stamp">PAID</div>
          </div>

          <div class="phone">
            <div class="phone-screen">
              <div class="ph-status"><span>9:41</span><span class="ph-notch"></span><span>●●●</span></div>
              <div class="ph-head">
                <div>
                  <div class="ph-hello">Good morning</div>
                  <div class="ph-town">City Hall</div>
                </div>
                <div class="ph-bell"><SiteIcon name="bell" :size="17" /><i></i></div>
              </div>

              <div class="ph-body">
                <div class="ph-due">
                  <div class="ph-due-label">Amount due</div>
                  <div class="ph-due-amount">$412.60</div>
                  <div class="ph-due-meta">3 bills · next due Nov 15</div>
                  <div class="ph-due-btn">Pay all</div>
                </div>

                <div class="ph-tiles">
                  <div v-for="t in phoneTiles" :key="t.label" class="ph-tile">
                    <span class="ph-tile-icon"><SiteIcon :name="t.icon" :size="17" /></span>
                    <span>{{ t.label }}</span>
                  </div>
                </div>

                <div class="ph-label">In progress</div>
                <div v-for="p in phoneProgress" :key="p.title" class="ph-row">
                  <div>
                    <div class="ph-row-title">{{ p.title }}</div>
                    <div class="ph-chip" :class="p.tone">{{ p.status }}</div>
                  </div>
                  <span class="ph-chev">›</span>
                </div>
              </div>

              <div class="ph-nav">
                <div v-for="(tab, i) in phoneTabs" :key="tab.label" class="ph-tab" :class="{ on: i === 0 }">
                  <SiteIcon :name="tab.icon" :size="18" />
                  <span>{{ tab.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="posted">
            <span class="posted-check"><SiteIcon name="check" :size="16" /></span>
            <div>
              <div class="posted-title">Posted to the ledger</div>
              <div class="posted-sub">Water &amp; sewer · Receipt A-20418</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== REPLACES ==================== -->
    <section class="replaces">
      <div class="mc-container replaces-inner">
        <span class="replaces-label">One contract replaces</span>
        <ul class="replaces-list">
          <li v-for="r in replaces" :key="r">{{ r }}</li>
        </ul>
      </div>
    </section>

    <!-- ==================== MODULES ==================== -->
    <section id="modules" class="mc-section">
      <div class="mc-container">
        <div class="mc-head modules-head">
          <div>
            <div class="mc-kicker" data-reveal>The platform</div>
            <h2 class="mc-title" data-reveal="1">Everything residents owe, and everything they ask for</h2>
          </div>
          <p class="mc-lead" data-reveal="2">
            Start with payments, then switch on water billing, parking, permits, recreation and the rest when you're
            ready. Every module shares one resident account, one ledger and one set of staff roles.
          </p>
        </div>

        <div class="module-grid">
          <article v-for="(m, i) in modules" :key="m.title" class="module" :data-reveal="(i % 3) + 1">
            <div class="module-top">
              <span class="module-no">{{ String(i + 1).padStart(2, "0") }}</span>
              <span class="module-icon"><SiteIcon :name="m.icon" :size="22" /></span>
            </div>
            <h3 class="module-title">{{ m.title }}</h3>
            <p class="module-text">{{ m.text }}</p>
            <ul class="module-points">
              <li v-for="p in m.points" :key="p">{{ p }}</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== THREE APPS ==================== -->
    <section class="mc-section mc-section-alt">
      <div class="mc-container">
        <div class="mc-head mc-head-center">
          <div class="mc-kicker" data-reveal>Three apps, one record</div>
          <h2 class="mc-title" data-reveal="1">Designed around how City Hall actually works</h2>
          <p class="mc-lead" data-reveal="2">
            A resident gets the job done in under a minute. Staff never retype anything. The officer on the street
            and the clerk at the counter see the same record.
          </p>
        </div>

        <div class="apps">
          <!-- Residents -->
          <article class="app-card" data-reveal="1">
            <div class="app-shot">
              <div class="shot-phone">
                <div class="shot-title">Pay</div>
                <div v-for="b in shotBills" :key="b[0]" class="shot-bill">
                  <span class="shot-box"><SiteIcon name="check" :size="12" :stroke="3" /></span>
                  <span class="shot-name">{{ b[0] }}</span>
                  <span class="shot-amt">{{ b[1] }}</span>
                </div>
                <div class="shot-pay">Pay $412.60</div>
              </div>
            </div>
            <div class="app-body">
              <div class="app-who"><SiteIcon name="users" :size="16" /> Residents</div>
              <h3 class="app-title">iPhone, Android and web</h3>
              <ul class="app-list">
                <li>Every linked bill and its amount due on the home screen</li>
                <li>Scan a parking slip's QR code to pay or contest</li>
                <li>Report a problem with a photo and a map pin</li>
                <li>Alerts for emergencies, closures and trash changes</li>
              </ul>
            </div>
          </article>

          <!-- Officers -->
          <article class="app-card" data-reveal="2">
            <div class="app-shot shot-dark">
              <div class="shot-handheld">
                <div class="hh-top">
                  <span>Write ticket</span>
                  <span class="hh-offline"><SiteIcon name="wifiOff" :size="12" /> Offline</span>
                </div>
                <div class="hh-plate">7XK 219</div>
                <div class="hh-flags">
                  <span class="hh-flag warn">2 unpaid tickets</span>
                  <span class="hh-flag">No permit</span>
                </div>
                <div class="hh-print"><SiteIcon name="printer" :size="15" /> Print ticket</div>
              </div>
            </div>
            <div class="app-body">
              <div class="app-who"><SiteIcon name="printer" :size="16" /> Parking officers</div>
              <h3 class="app-title">The ticket handheld</h3>
              <ul class="app-list">
                <li>Read the plate with the camera, right on the device</li>
                <li>Print on Zebra Bluetooth printers</li>
                <li>Works with no signal; tickets upload on their own</li>
                <li>Plate check, tow list and electronic chalking</li>
              </ul>
            </div>
          </article>

          <!-- City Hall -->
          <article class="app-card" data-reveal="3">
            <div class="app-shot">
              <div class="shot-table">
                <div class="tb-head">
                  <span>Collector queue</span>
                  <span class="tb-count">14 to post</span>
                </div>
                <div v-for="r in shotQueue" :key="r[0]" class="tb-row">
                  <span class="tb-name">{{ r[0] }}</span>
                  <span class="tb-amt">{{ r[1] }}</span>
                  <span class="tb-chip" :class="r[3]">{{ r[2] }}</span>
                </div>
              </div>
            </div>
            <div class="app-body">
              <div class="app-who"><SiteIcon name="city" :size="16" /> City Hall staff</div>
              <h3 class="app-title">The staff dashboard</h3>
              <ul class="app-list">
                <li>A queue for the Collector, Clerk, DPW and Parking</li>
                <li>Payment posting and daily deposit reports</li>
                <li>Permit review, inspections and the hearing room</li>
                <li>Staff tools stay on the web, never on personal phones</li>
              </ul>
            </div>
          </article>
        </div>

        <div class="admin-strip" data-reveal>
          <div class="admin-strip-title">
            <SiteIcon name="shield" :size="20" />
            <span><b>And for admins:</b> change it yourself, no vendor ticket.</span>
          </div>
          <ul class="admin-strip-list">
            <li>Access per role and per person</li>
            <li>Fee schedules with versions</li>
            <li>City name, seal and hours</li>
            <li>Audit log and CSV exports</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ==================== CARD FEES (receipt calculator) ==================== -->
    <section class="mc-section fees">
      <div class="mc-container fees-layout">
        <div class="fees-copy">
          <div class="mc-kicker" data-reveal>Card fees off your books</div>
          <h2 class="mc-title" data-reveal="1">Stop paying to get paid</h2>
          <p class="mc-lead" data-reveal="2">
            Many towns quietly absorb card processing on every online payment. With Municipal Collectionz the payer
            covers it, and sees the exact amount before they pay. Your deposit is the full bill, every time.
          </p>
          <ul class="fees-list" data-reveal="3">
            <li><span>Card payments</span><b>{{ pricing.cardFee }}, paid by the resident</b></li>
            <li><span>Bank payments</span><b>{{ pricing.bankFee }}, paid by the resident</b></li>
            <li><span>Platform fee</span><b>{{ pricing.platformFee }} per payment, paid by the resident</b></li>
          </ul>
          <p class="fees-note" data-reveal="4">
            Where your state doesn't allow convenience fees, we price processing into the subscription instead.
          </p>
        </div>

        <div class="receipt" data-reveal="2">
          <div class="receipt-head">
            <BrandMark :size="30" />
            <div>
              <div class="receipt-title">Collections summary</div>
              <div class="receipt-sub">Estimate for one year</div>
            </div>
          </div>

          <label class="receipt-label" for="calc-amount">Online payments your city takes in a year</label>
          <div class="receipt-input">
            <span>$</span>
            <input
              id="calc-amount"
              v-model="calcInput"
              inputmode="numeric"
              autocomplete="off"
              @blur="formatCalcInput"
            />
          </div>
          <input
            v-model.number="calcAmount"
            class="receipt-range"
            type="range"
            min="250000"
            max="40000000"
            step="250000"
            aria-label="Online payments per year"
          />

          <div class="receipt-lines">
            <div class="r-line"><span>Online collections</span><i></i><b>{{ money(calcAmount) }}</b></div>
            <div class="r-line"><span>Card processing at about 2.95%</span><i></i><b>{{ money(calcSavings) }}</b></div>
            <div class="r-line r-credit"><span>Paid by residents at checkout</span><i></i><b>−{{ money(calcSavings) }}</b></div>
          </div>

          <div class="receipt-total">
            <span>Card fees your city pays</span>
            <b>$0</b>
          </div>
          <div class="receipt-saved">
            You stop absorbing <b>{{ money(calcSavings) }}</b> a year.
          </div>
          <div class="mc-stamp receipt-stamp">PAID</div>
        </div>
      </div>
    </section>

    <!-- ==================== WHO IT'S FOR ==================== -->
    <section class="mc-section mc-section-alt">
      <div class="mc-container">
        <div class="mc-head">
          <div class="mc-kicker" data-reveal>Who it's for</div>
          <h2 class="mc-title" data-reveal="1">Built for every office that takes a payment</h2>
          <p class="mc-lead" data-reveal="2">The admin decides what each person can see and do, office by office and person by person.</p>
        </div>

        <div class="aud-grid">
          <article v-for="(a, i) in audiences" :key="a.who" class="aud" :data-reveal="(i % 2) + 1">
            <div class="aud-who"><SiteIcon :name="a.icon" :size="18" /> {{ a.who }}</div>
            <h3 class="aud-title">{{ a.title }}</h3>
            <p class="aud-text">{{ a.text }}</p>
            <ul class="aud-points">
              <li v-for="p in a.points" :key="p"><SiteIcon name="check" :size="16" /> {{ p }}</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== SECURITY ==================== -->
    <section class="mc-section security">
      <div class="mc-container">
        <div class="mc-head security-head">
          <div>
            <div class="mc-kicker kicker-light" data-reveal>Secure by design</div>
            <h2 class="mc-title title-light" data-reveal="1">Built for public money and public records</h2>
          </div>
          <p class="mc-lead lead-light" data-reveal="2">
            Least-privilege staff roles, a full audit trail, and card numbers that never touch City Hall's systems
            or ours.
          </p>
        </div>

        <div class="sec-grid">
          <div v-for="(t, i) in techFeatures" :key="t.title" class="sec-item" :data-reveal="(i % 4) + 1">
            <SiteIcon :name="t.icon" :size="24" class="sec-icon" />
            <h4 class="sec-title">{{ t.title }}</h4>
            <p class="sec-text">{{ t.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== PRICING ==================== -->
    <section id="pricing" class="mc-section">
      <div class="mc-container">
        <div class="mc-head mc-head-center">
          <div class="mc-kicker" data-reveal>Pricing</div>
          <h2 class="mc-title" data-reveal="1">Priced by the size of your city</h2>
          <p class="mc-lead" data-reveal="2">
            Every module, every resident account and every staff seat included. Residents pay a
            {{ pricing.platformFee }} platform fee on each online payment, plus the card or bank fee.
          </p>
        </div>

        <div class="tiers">
          <div
            v-for="(tier, i) in pricing.tiers"
            :key="tier.size"
            class="tier"
            :class="{ featured: tier.popular }"
            :data-reveal="i + 1"
          >
            <div class="tier-size">
              {{ tier.size }}
              <span v-if="tier.popular" class="tier-tag">Typical town</span>
            </div>
            <div class="tier-price">
              <template v-if="tier.yearly !== 'Custom'">
                <span class="tier-amount">{{ tier.yearly }}</span>
                <span class="tier-per">per year</span>
              </template>
              <span v-else class="tier-amount">Let's talk</span>
            </div>
            <div class="tier-setup">
              {{ tier.setup === "Custom" ? "Custom setup and data migration" : tier.setup + " one-time setup" }}
            </div>
            <router-link
              to="/demo"
              class="mc-btn mc-btn-block"
              :class="tier.popular ? 'mc-btn-primary' : 'mc-btn-outline'"
            >
              {{ tier.yearly === "Custom" ? "Contact us" : "Request a demo" }}
            </router-link>
          </div>
        </div>

        <div class="pricing-foot">
          <div class="fee-only" data-reveal>
            <span class="fee-only-icon"><SiteIcon name="coins" :size="22" /></span>
            <div>
              <div class="fee-only-title">{{ pricing.feeOnly.title }}</div>
              <p class="fee-only-text">{{ pricing.feeOnly.text }}</p>
            </div>
          </div>
          <div class="included" data-reveal="1">
            <div class="included-title">Included with every plan</div>
            <ul>
              <li v-for="item in included" :key="item"><SiteIcon name="check" :size="16" /> {{ item }}</li>
            </ul>
          </div>
        </div>
        <p class="pricing-terms">{{ pricing.contract }}. Officer printers available through us, or use your own.</p>
      </div>
    </section>

    <!-- ==================== FAQ ==================== -->
    <section class="mc-section mc-section-alt">
      <div class="mc-container faq-layout">
        <div class="faq-side">
          <div class="mc-kicker" data-reveal>Questions</div>
          <h2 class="mc-title" data-reveal="1">Questions a treasurer should ask</h2>
          <p class="mc-lead" data-reveal="2">
            Something else on your mind? Call us at
            <a :href="contact.phoneHref" class="mc-link inline-link">{{ contact.phoneLabel }}</a>.
          </p>
        </div>

        <div class="faq-list">
          <div v-for="(q, i) in faqs" :key="q.q" class="faq" :class="{ open: openFaq === i }">
            <button
              type="button"
              class="faq-q"
              :aria-expanded="openFaq === i ? 'true' : 'false'"
              @click="openFaq = openFaq === i ? -1 : i"
            >
              <span>{{ q.q }}</span>
              <span class="faq-sign"><SiteIcon :name="openFaq === i ? 'minus' : 'plus'" :size="18" /></span>
            </button>
            <div v-show="openFaq === i" class="faq-a">{{ q.a }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== FINAL CTA ==================== -->
    <section class="final-cta">
      <div class="cta-seal" aria-hidden="true"><BrandMark :size="420" on="dark" /></div>
      <div class="mc-container cta-inner" data-reveal>
        <h2 class="cta-title">Ready to collect every dollar in one place?</h2>
        <p class="cta-sub">
          See the resident app, the officer handheld and the City Hall dashboard working on your own bills.
        </p>
        <div class="cta-actions">
          <router-link to="/demo" class="mc-btn mc-btn-light">
            <SiteIcon name="calendarCheck" :size="20" />
            Schedule your demo
          </router-link>
          <a :href="contact.phoneHref" class="cta-phone"><SiteIcon name="phone" :size="18" /> {{ contact.phoneLabel }}</a>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import "@/styles/marketing.css";
import AppNavigation from "@/components/AppNavigation.vue";
import AppFooter from "@/components/AppFooter.vue";
import BrandMark from "@/components/BrandMark.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import reveal from "@/mixins/reveal";
import { CONTACT, PRICING } from "@/config/site";

// Fixed pattern for the decorative QR code on the parking slip (7 × 7 grid).
const QR = new Set([1, 2, 3, 5, 7, 8, 10, 12, 14, 15, 16, 17, 19, 21, 23, 25, 26, 29, 30, 31, 33, 36, 37, 39, 40, 43, 44, 45, 46, 47, 49]);

export default {
  name: "HomeView",
  components: { AppNavigation, AppFooter, BrandMark, SiteIcon },
  mixins: [reveal],

  data() {
    return {
      openFaq: 0,
      calcAmount: 8000000,
      calcInput: "8,000,000",
      pricing: PRICING,
      contact: CONTACT,

      heroFigures: [
        { label: "To pay a bill", value: "< 1 min" },
        { label: "To pay a ticket", value: "2 taps" },
        { label: "Card fees for the city", value: "$0" },
      ],

      slipRows: [
        ["Plate", "7XK 219"],
        ["Violation", "Expired meter"],
        ["Location", "Main St, 400 blk"],
        ["Fine", "$40.00"],
      ],

      phoneTiles: [
        { label: "Report", icon: "alert" },
        { label: "Pay", icon: "card" },
        { label: "Permit", icon: "permit" },
        { label: "Trash", icon: "calendar" },
      ],
      phoneProgress: [
        { title: "Pothole on Elm Street", status: "Being worked on", tone: "chip-green" },
        { title: "Deck permit", status: "We need something from you", tone: "chip-amber" },
      ],
      phoneTabs: [
        { label: "Home", icon: "home" },
        { label: "Pay", icon: "card" },
        { label: "Report", icon: "alert" },
        { label: "Services", icon: "grid" },
        { label: "Account", icon: "users" },
      ],

      replaces: [
        "Utility billing portal",
        "Parking ticket vendor",
        "311 app",
        "Permit software",
        "Records request inbox",
        "Card fees at the counter",
      ],

      modules: [
        {
          icon: "card",
          title: "Bills & Online Payments",
          text: "Water and sewer, property tax, motor vehicle excise and every city fee, paid in one checkout.",
          points: ["Residents link bills by account number", "Auto-pay for water bills", "Payment links for anything that isn't a bill"],
        },
        {
          icon: "droplet",
          title: "Water & Sewer Billing",
          text: "Accounts, rate tables, meter reads and billing runs, for towns and the special districts that bill water.",
          points: ["Meter read routes on the phone", "Emailed or mailed bills", "Compare a cycle with your old system first"],
        },
        {
          icon: "ticket",
          title: "Parking Tickets",
          text: "Officers write and print tickets on the street. Drivers scan the slip and pay or contest in two taps.",
          points: ["Offline ticket book with printing", "Plate check, permits and tow list", "Electronic chalking with timers"],
        },
        {
          icon: "alert",
          title: "Report a Problem (311)",
          text: "Potholes, streetlights, graffiti and missed trash, pinned on a map with photos and sent to the right office.",
          points: ["Status residents can follow", "A queue for DPW and every office", "Photo and location on every request"],
        },
        {
          icon: "permit",
          title: "Permits & Inspections",
          text: "Apply, pay, upload plans and book inspections online. Staff review, inspect and issue in one place.",
          points: ["Permit types and fees you set", "Inspection scheduling and results", "Certificates issued from the record"],
        },
        {
          icon: "file",
          title: "Records & Certificates",
          text: "Public records requests with legal deadlines tracked, plus birth, death and marriage certificates.",
          points: ["Deadline clock on every request", "Lien certificates and abatements", "Pay and download in the app"],
        },
        {
          icon: "video",
          title: "Hearings by Video",
          text: "Residents request a hearing on a ticket or bill and join it live from their phone.",
          points: ["Hearing requests and scheduling", "Live video hearing room for staff", "Decisions update the bill"],
        },
        {
          icon: "calendarCheck",
          title: "Parks & Recreation",
          text: "Program sign-ups, facility rentals and season passes, with resident and non-resident prices.",
          points: ["Waitlists and sibling discounts", "Residency checks for resident prices", "Pool and beach passes with a QR gate check"],
        },
        {
          icon: "bank",
          title: "Finance & Grants",
          text: "The town's books in the same system that takes the money: budget, purchasing, payroll posting and grants.",
          points: ["Ledger, budget building and bank reconciliation", "Purchase orders, check and ACH payment runs", "A public spending page for residents"],
        },
        {
          icon: "home",
          title: "Code Enforcement & Rentals",
          text: "Property complaints to cases, notices and fines, plus short-term rental registration and occupancy tax.",
          points: ["Inspections on the phone, even offline", "Notices, fines, hearings and liens", "Short-term rental registration and occupancy tax"],
        },
        {
          icon: "bell",
          title: "Alerts & City Info",
          text: "Emergencies, road closures and trash changes pushed to phones, translated, by the topics residents pick.",
          points: ["Emergency and topic alerts", "City calendar and public meetings", "Trash and recycling schedules"],
        },
        {
          icon: "shield",
          title: "Dashboard, Roles & Audit",
          text: "One web dashboard where every office works its own queue. The admin decides who can do what, and every change is logged.",
          points: ["Revenue reports by fund", "Per-role and per-person access", "Full audit log of staff actions"],
        },
      ],

      shotBills: [
        ["Water & sewer", "$186.20"],
        ["Property tax Q2", "$214.40"],
        ["Excise 2026", "$12.00"],
      ],
      shotQueue: [
        ["Water #40-1182", "$186.20", "Posted", "c-ok"],
        ["Excise #22917", "$12.00", "Posted", "c-ok"],
        ["Tax #0083-4", "$2,140.00", "Review", "c-wait"],
        ["Ticket #100482", "$40.00", "Posted", "c-ok"],
      ],

      audiences: [
        {
          icon: "users",
          who: "Residents",
          title: "City Hall in their pocket",
          text: "Everything they owe and everything they've asked for, in one place, in plain language.",
          points: ["Pay any bill in under a minute", "Follow requests and permits", "Join a hearing from home", "Alerts in their language"],
        },
        {
          icon: "coins",
          who: "Treasurers & collectors",
          title: "More collected, less chasing",
          text: "More residents pay online and on time, and every payment lands in the ledger already matched.",
          points: ["No card fees on your books", "Automatic posting and deposits", "Revenue reports by fund", "Fewer calls and counter visits"],
        },
        {
          icon: "file",
          who: "Clerks & departments",
          title: "One queue per office",
          text: "Permits, records, certificates and 311 requests arrive complete, with photos and payment done.",
          points: ["Records deadlines tracked", "Inspections booked online", "Certificates from the record", "Hand-offs without email"],
        },
        {
          icon: "car",
          who: "Parking & police",
          title: "Tickets that pay themselves",
          text: "Officers write tickets in seconds, drivers pay from the slip, and appeals run online.",
          points: ["Offline handheld with printing", "Plate check and tow list", "Electronic chalking", "Video hearings for appeals"],
        },
      ],

      techFeatures: [
        { icon: "users", title: "Role-based access", text: "Each office and each person sees only what the admin allows." },
        { icon: "history", title: "Full audit log", text: "Every staff action is recorded with who, what and when." },
        { icon: "card", title: "Payments by Stripe", text: "Card numbers go straight to Stripe and never touch City Hall." },
        { icon: "lock", title: "Encrypted everywhere", text: "Data is encrypted in transit and at rest on Google Cloud." },
        { icon: "shield", title: "Staff tools on the web", text: "Dashboards aren't on personal phones. Only the officer handheld is." },
        { icon: "wifiOff", title: "Works offline", text: "Tickets, meter reads and code inspections save on the device and sync when a signal returns." },
        { icon: "translate", title: "Translated alerts", text: "Residents get alerts in the language they choose." },
        { icon: "check", title: "Accessible", text: "Large tap targets and high contrast for residents of every age." },
      ],

      included: [
        "Every module",
        "Unlimited residents and staff",
        "iPhone, Android and web apps",
        "Your city's name and seal",
        "Setup, data import and training",
        "Email and phone support",
      ],

      faqs: [
        {
          q: "Who pays the card fee?",
          a: "The resident does, and they see the exact amount before they pay. Your city deposits the full bill amount. If your state doesn't allow convenience fees, we fold processing into your subscription instead.",
        },
        {
          q: "Where does the money go?",
          a: "Straight to your city. We set up a Stripe account in your city's name, and resident payments are deposited to your city's own bank account. Our platform fee is taken at the time of each payment, so we never hold your city's money.",
        },
        {
          q: "Do we have to switch everything at once?",
          a: "No. You can start with online payments only, then turn on parking, permits, records and hearings one at a time. Every module uses the same resident accounts and staff roles.",
        },
        {
          q: "Can it work with our current billing system?",
          a: "Yes. We start with file imports and exports from your billing system, so bills appear in the app and payments flow back. Live connections are available for the most common systems.",
        },
        {
          q: "What if an officer loses signal?",
          a: "The handheld keeps working. Tickets are written, printed and saved on the device, then upload on their own when a signal returns.",
        },
        {
          q: "Is the app branded for our city?",
          a: "Yes. Residents see your city's name and seal throughout the app and on receipts, emails and printed slips.",
        },
      ],
    };
  },

  computed: {
    calcSavings() {
      return Math.round((this.calcAmount || 0) * 0.0295);
    },
  },

  watch: {
    calcAmount(v) {
      this.calcInput = Number(v || 0).toLocaleString("en-US");
    },
    calcInput(v) {
      const n = Number(String(v).replace(/[^0-9]/g, ""));
      if (!Number.isNaN(n) && n !== this.calcAmount) this.calcAmount = n;
    },
  },

  methods: {
    qrOn(n) {
      return QR.has(n);
    },
    money(n) {
      return "$" + Number(n || 0).toLocaleString("en-US");
    },
    formatCalcInput() {
      this.calcInput = Number(this.calcAmount || 0).toLocaleString("en-US");
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<style scoped>
/* ==================== HERO ==================== */
.hero {
  position: relative;
  overflow: hidden;
  padding: 72px 0 104px;
  border-bottom: 1px solid var(--rule);
}

.hero-ledger {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: repeating-linear-gradient(to bottom, transparent 0, transparent 35px, var(--ledger) 35px, var(--ledger) 36px);
  mask-image: linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%);
}

.hero-ledger::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: max(24px, calc(50% - 600px - 28px));
  width: 5px;
  border-left: 1px solid rgba(168, 117, 47, 0.28);
  border-right: 1px solid rgba(168, 117, 47, 0.28);
}

.hero-inner {
  position: relative;
  display: grid;
  grid-template-columns: 1.02fr 0.98fr;
  gap: 48px;
  align-items: center;
}

.hero-title {
  font-size: clamp(2.5rem, 5.2vw, 4rem);
  line-height: 1.04;
  letter-spacing: -0.025em;
  margin-bottom: 24px;
  font-weight: 600;
  text-wrap: balance;
}

.hero-sub {
  max-width: 540px;
  margin-bottom: 34px;
  font-size: 1.14rem;
  line-height: 1.65;
  color: var(--ink-2);
}

.hero-ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 44px;
}

.hero-figures {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
  border-top: 1px solid var(--rule-strong);
  max-width: 540px;
}

.figure {
  flex: 1;
  min-width: 140px;
  padding: 16px 20px 0 0;
}

.figure + .figure {
  padding-left: 20px;
  border-left: 1px solid var(--rule);
}

.figure dt {
  font-size: 0.82rem;
  color: var(--muted);
  order: 2;
}

.figure dd {
  margin: 0 0 2px;
  font-family: var(--serif);
  font-size: 2rem;
  font-weight: 600;
  color: var(--ink);
}

.figure {
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
}

/* ---------- Hero composition ---------- */
.hero-visual {
  position: relative;
  height: 620px;
}

.slip {
  position: absolute;
  top: 70px;
  left: 0;
  width: 250px;
  padding: 22px 20px 20px;
  background: #fffdf8;
  border: 1px solid var(--rule);
  border-radius: 4px;
  box-shadow: 0 30px 60px rgba(14, 34, 54, 0.14), 0 2px 6px rgba(14, 34, 54, 0.06);
  transform: rotate(-7deg);
  font-family: var(--sans);
}

.slip-perf {
  position: absolute;
  top: -6px;
  left: 0;
  right: 0;
  height: 12px;
  background: radial-gradient(circle at 6px 6px, var(--paper) 4px, transparent 4.5px) repeat-x;
  background-size: 14px 12px;
}

.slip-kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}

.slip-no {
  font-family: var(--serif);
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 2px 0 14px;
}

.slip-rows {
  display: grid;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px dashed var(--rule-strong);
  border-bottom: 1px dashed var(--rule-strong);
}

.slip-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-2);
}

.slip-row i {
  flex: 1;
  border-bottom: 1px dotted var(--rule-strong);
  transform: translateY(-3px);
}

.slip-row b {
  color: var(--ink);
  font-weight: 700;
}

.slip-qr {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  width: 84px;
  margin: 16px auto 8px;
}

.slip-qr span {
  aspect-ratio: 1;
  background: transparent;
}

.slip-qr span.on {
  background: var(--ink);
}

.slip-foot {
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}

.slip-stamp {
  position: absolute;
  top: 120px;
  right: 18px;
  font-size: 30px;
  padding: 6px 16px 4px;
  transform: rotate(-18deg);
}

.phone {
  position: absolute;
  top: 0;
  right: 24px;
  width: 290px;
  height: 596px;
  padding: 10px;
  border-radius: 44px;
  background: #101c29;
  box-shadow: 0 0 0 1.5px #2b3a4a, 0 40px 80px rgba(14, 34, 54, 0.3), 0 8px 20px rgba(14, 34, 54, 0.15);
  transform: rotate(3deg);
  animation: phone-float 8s ease-in-out infinite;
}

@keyframes phone-float {
  0%, 100% { transform: rotate(3deg) translateY(0); }
  50% { transform: rotate(3deg) translateY(-10px); }
}

.phone-screen {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 34px;
  background: var(--paper);
}

.ph-status {
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: 10px 20px 4px;
  background: var(--ink);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.ph-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 104px;
  height: 20px;
  border-radius: 0 0 14px 14px;
  background: #101c29;
}

.ph-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 18px 24px;
  background: var(--ink);
  color: #fff;
}

.ph-hello {
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.65);
}

.ph-town {
  font-family: var(--serif);
  font-size: 21px;
  font-weight: 600;
  line-height: 1.15;
}

.ph-bell {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ph-bell i {
  position: absolute;
  top: 8px;
  right: 9px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7cc4a5;
}

.ph-body {
  padding: 0 12px;
  margin-top: -14px;
}

.ph-due {
  padding: 14px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(14, 34, 54, 0.1);
}

.ph-due-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.ph-due-amount {
  font-family: var(--serif);
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ink);
}

.ph-due-meta {
  font-size: 11px;
  color: var(--muted);
  margin-bottom: 10px;
}

.ph-due-btn {
  padding: 9px;
  border-radius: 8px;
  background: var(--green);
  color: #fff;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
}

.ph-tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin: 12px 0 14px;
}

.ph-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 600;
  color: var(--ink-2);
}

.ph-tile-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card);
  border: 1px solid var(--rule);
  color: var(--green);
}

.ph-label {
  margin-bottom: 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.ph-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--rule);
}

.ph-row-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink);
}

.ph-chip {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 600;
}

.chip-green {
  background: var(--green-tint);
  color: var(--green-2);
}

.chip-amber {
  background: #f6e7cc;
  color: #7a5414;
}

.ph-chev {
  font-size: 18px;
  color: #a2abb4;
}

.ph-nav {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-around;
  padding: 9px 4px 16px;
  background: #fff;
  border-top: 1px solid var(--rule);
}

.ph-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 600;
  color: #9aa3ad;
}

.ph-tab.on {
  color: var(--green);
}

.posted {
  position: absolute;
  bottom: 40px;
  left: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 12px 12px;
  border-radius: 10px;
  background: var(--ink);
  color: var(--on-navy);
  box-shadow: 0 20px 40px rgba(14, 34, 54, 0.25);
}

.posted-check {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.posted-title {
  font-size: 13px;
  font-weight: 700;
}

.posted-sub {
  font-size: 11.5px;
  color: var(--on-navy-2);
}

/* ==================== REPLACES ==================== */
.replaces {
  background: var(--ink);
  color: var(--on-navy);
  padding: 22px 0;
}

.replaces-inner {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.replaces-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #7cc4a5;
}

.replaces-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 26px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.replaces-list li {
  font-size: 0.96rem;
  color: var(--on-navy-2);
  text-decoration: line-through;
  text-decoration-color: #7cc4a5;
  text-decoration-thickness: 1.5px;
}

/* ==================== MODULES ==================== */
.modules-head {
  max-width: none;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 48px;
  align-items: end;
}

.modules-head .mc-title {
  margin-bottom: 0;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-top: 1px solid var(--rule-strong);
  border-left: 1px solid var(--rule);
}

.module {
  position: relative;
  padding: 32px 30px 34px;
  border-right: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: transparent;
  transition: background 0.25s ease, opacity 0.7s ease, transform 0.7s ease;
}

.module::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--green);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.module:hover {
  background: var(--card);
}

.module:hover::before {
  transform: scaleX(1);
}

.module-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 26px;
}

.module-no {
  font-family: var(--serif);
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--green);
}

.module-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid var(--rule-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink);
}

.module-title {
  font-size: 1.5rem;
  line-height: 1.2;
  margin-bottom: 10px;
}

.module-text {
  font-size: 0.97rem;
  line-height: 1.6;
  color: var(--ink-2);
  margin-bottom: 16px;
}

.module-points {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 7px;
}

.module-points li {
  position: relative;
  padding-left: 18px;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--muted);
}

.module-points li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 8px;
  height: 2px;
  background: var(--green);
}

/* ==================== THREE APPS ==================== */
.apps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.app-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--rule);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(14, 34, 54, 0.04);
  transition: box-shadow 0.25s ease, opacity 0.7s ease, transform 0.7s ease;
}

.app-card:hover {
  box-shadow: 0 18px 40px rgba(14, 34, 54, 0.1);
}

.app-shot {
  height: 230px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--paper);
  background-image: repeating-linear-gradient(to bottom, transparent 0, transparent 23px, var(--ledger) 23px, var(--ledger) 24px);
  border-bottom: 1px solid var(--rule);
}

.app-shot.shot-dark {
  background: var(--ink);
  background-image: none;
}

.shot-phone,
.shot-table {
  width: 100%;
  max-width: 270px;
  padding: 14px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--rule);
  box-shadow: 0 12px 28px rgba(14, 34, 54, 0.1);
}

.shot-title {
  font-family: var(--serif);
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
}

.shot-bill {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 0;
  border-bottom: 1px solid var(--rule);
  font-size: 12.5px;
}

.shot-box {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: var(--green);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shot-name {
  flex: 1;
  color: var(--ink-2);
}

.shot-amt {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.shot-pay {
  margin-top: 10px;
  padding: 8px;
  border-radius: 7px;
  background: var(--green);
  color: #fff;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
}

.shot-handheld {
  width: 100%;
  max-width: 230px;
  padding: 14px;
  border-radius: 18px;
  background: #16293d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--on-navy);
}

.hh-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 10px;
}

.hh-offline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(243, 237, 224, 0.12);
  font-size: 10px;
  font-weight: 600;
}

.hh-plate {
  padding: 8px;
  border-radius: 6px;
  background: #f3ede0;
  color: var(--ink);
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.12em;
  border: 2px solid #c9bfa8;
}

.hh-flags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin: 10px 0 12px;
}

.hh-flag {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(243, 237, 224, 0.12);
  font-size: 10.5px;
  font-weight: 600;
}

.hh-flag.warn {
  background: #f6e7cc;
  color: #7a5414;
}

.hh-print {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border-radius: 8px;
  background: var(--green);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.tb-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-family: var(--serif);
  font-size: 15px;
  font-weight: 700;
}

.tb-count {
  font-family: var(--sans);
  font-size: 10.5px;
  font-weight: 700;
  color: var(--green);
}

.tb-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 8px;
  align-items: center;
  padding: 7px 0;
  border-top: 1px solid var(--rule);
  font-size: 11.5px;
}

.tb-name {
  color: var(--ink-2);
}

.tb-amt {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.tb-chip {
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
}

.c-ok {
  background: var(--green-tint);
  color: var(--green-2);
}

.c-wait {
  background: #f6e7cc;
  color: #7a5414;
}

.app-body {
  padding: 26px 26px 30px;
}

.app-who {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--green);
}

.app-title {
  font-size: 1.55rem;
  margin-bottom: 14px;
}

.app-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 9px;
}

.app-list li {
  position: relative;
  padding-left: 18px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--ink-2);
}

.app-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.6em;
  width: 8px;
  height: 2px;
  background: var(--green);
}

.admin-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px 32px;
  flex-wrap: wrap;
  margin-top: 22px;
  padding: 20px 26px;
  border-radius: 10px;
  background: var(--ink);
  color: var(--on-navy);
}

.admin-strip-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
}

.admin-strip-title .site-icon {
  color: #7cc4a5;
}

.admin-strip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.admin-strip-list li {
  font-size: 0.9rem;
  color: var(--on-navy-2);
}

.admin-strip-list li::before {
  content: "✓ ";
  color: #7cc4a5;
  font-weight: 700;
}

/* ==================== FEES / RECEIPT ==================== */
.fees-layout {
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 64px;
  align-items: center;
}

.fees-layout > *,
.hero-inner > *,
.faq-layout > * {
  min-width: 0;
}

.fees-copy .mc-lead {
  margin-bottom: 26px;
}

.fees-list {
  margin: 0 0 18px;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--rule-strong);
}

.fees-list li {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--rule);
  font-size: 0.97rem;
}

.fees-list span {
  color: var(--muted);
}

.fees-list b {
  font-weight: 600;
  text-align: right;
}

.fees-note {
  font-size: 0.88rem;
  line-height: 1.6;
  color: var(--muted);
}

.receipt {
  position: relative;
  padding: 30px 30px 40px;
  background: var(--card);
  border: 1px solid var(--rule);
  border-bottom: 0;
  box-shadow: 0 30px 60px rgba(14, 34, 54, 0.12);
  font-variant-numeric: tabular-nums;
}

.receipt::after {
  content: "";
  position: absolute;
  left: -1px;
  right: -1px;
  bottom: -12px;
  height: 12px;
  background: linear-gradient(-45deg, transparent 8px, var(--card) 0) 0 0 / 16px 12px repeat-x,
    linear-gradient(45deg, transparent 8px, var(--card) 0) 0 0 / 16px 12px repeat-x;
}

.receipt-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px dashed var(--rule-strong);
}

.receipt-title {
  font-family: var(--serif);
  font-size: 1.25rem;
  font-weight: 700;
}

.receipt-sub {
  font-size: 0.82rem;
  color: var(--muted);
}

.receipt-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--ink-2);
}

.receipt-input {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 56px;
  padding: 0 16px;
  border: 1.5px solid var(--rule-strong);
  border-radius: 6px;
  background: #fff;
  margin-bottom: 14px;
}

.receipt-input:focus-within {
  border-color: var(--green);
  box-shadow: 0 0 0 3px rgba(30, 107, 82, 0.15);
}

.receipt-input span {
  font-size: 1.35rem;
  color: var(--muted);
}

.receipt-input input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: var(--sans);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--ink);
}

.receipt-range {
  width: 100%;
  margin-bottom: 22px;
  accent-color: var(--green);
}

.receipt-lines {
  display: grid;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px dashed var(--rule-strong);
}

.r-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.93rem;
  color: var(--ink-2);
}

.r-line i {
  flex: 1;
  border-bottom: 1px dotted var(--rule-strong);
  transform: translateY(-4px);
}

.r-line b {
  font-weight: 600;
  color: var(--ink);
}

.r-credit b {
  color: var(--green);
}

.receipt-total {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 16px;
  font-weight: 700;
}

.receipt-total b {
  font-family: var(--serif);
  font-size: 2.4rem;
  color: var(--ink);
}

.receipt-saved {
  margin-top: 6px;
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--green-tint);
  color: var(--green-2);
  font-size: 0.93rem;
}

.receipt-stamp {
  position: absolute;
  top: 26px;
  right: 22px;
  font-size: 20px;
}

/* ==================== AUDIENCES ==================== */
.aud-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
}

.aud {
  padding: 30px 32px 32px;
  background: var(--card);
  border: 1px solid var(--rule);
  border-left: 4px solid var(--green);
  border-radius: 4px 10px 10px 4px;
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.aud-who {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--green);
}

.aud-title {
  font-size: 1.6rem;
  margin-bottom: 8px;
}

.aud-text {
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--ink-2);
  margin-bottom: 18px;
}

.aud-points {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px 18px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.aud-points li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--ink-2);
}

.aud-points .site-icon {
  color: var(--green);
  margin-top: 1px;
}

/* ==================== SECURITY ==================== */
.security {
  background: var(--ink);
}

.security-head {
  max-width: none;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 48px;
  align-items: end;
}

.security-head .mc-title {
  margin-bottom: 0;
}

.mc-site .title-light {
  color: var(--on-navy);
}

.mc-site .lead-light {
  color: var(--on-navy-2);
}

.kicker-light {
  color: #7cc4a5;
}

.sec-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid rgba(243, 237, 224, 0.16);
}

.sec-item {
  padding: 30px 24px 34px 0;
  border-bottom: 1px solid rgba(243, 237, 224, 0.1);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.sec-item:not(:nth-child(4n + 1)) {
  padding-left: 24px;
  border-left: 1px solid rgba(243, 237, 224, 0.1);
}

.sec-icon {
  color: #7cc4a5;
  margin-bottom: 16px;
}

.mc-site .sec-title {
  color: var(--on-navy);
  font-size: 1.3rem;
  margin-bottom: 6px;
}

.sec-text {
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--on-navy-2);
}

/* ==================== PRICING ==================== */
.tiers {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 22px;
}

.tier {
  display: flex;
  flex-direction: column;
  padding: 28px 24px 24px;
  background: var(--card);
  border: 1px solid var(--rule);
  border-radius: 10px;
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.tier.featured {
  background: var(--ink);
  border-color: var(--ink);
  box-shadow: 0 24px 50px rgba(14, 34, 54, 0.22);
}

.tier-size {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 22px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--rule);
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink-2);
}

.featured .tier-size {
  color: var(--on-navy);
  border-bottom-color: rgba(243, 237, 224, 0.16);
}

.tier-tag {
  padding: 3px 9px;
  border-radius: 999px;
  background: #7cc4a5;
  color: var(--ink);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.tier-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.tier-amount {
  font-family: var(--serif);
  font-size: 2.55rem;
  font-weight: 600;
  line-height: 1;
  color: var(--ink);
}

.featured .tier-amount {
  color: var(--on-navy);
}

.tier-per {
  font-size: 0.88rem;
  color: var(--muted);
}

.featured .tier-per,
.featured .tier-setup {
  color: var(--on-navy-2);
}

.tier-setup {
  flex: 1;
  margin-bottom: 24px;
  font-size: 0.88rem;
  color: var(--muted);
}

.pricing-foot {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 18px;
}

.fee-only {
  display: flex;
  gap: 16px;
  padding: 24px;
  border: 1.5px dashed rgba(30, 107, 82, 0.45);
  border-radius: 10px;
  background: var(--green-tint);
}

.fee-only-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fee-only-title {
  font-family: var(--serif);
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.fee-only-text {
  font-size: 0.93rem;
  line-height: 1.55;
  color: var(--ink-2);
}

.included {
  padding: 22px 26px;
  border: 1px solid var(--rule);
  border-radius: 10px;
  background: var(--card);
}

.included-title {
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
}

.included ul {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.included li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.93rem;
  color: var(--ink-2);
}

.included .site-icon {
  color: var(--green);
}

.pricing-terms {
  margin-top: 20px;
  text-align: center;
  font-size: 0.86rem;
  color: var(--muted);
}

/* ==================== FAQ ==================== */
.faq-layout {
  display: grid;
  grid-template-columns: 0.8fr 1.2fr;
  gap: 64px;
  align-items: start;
}

.inline-link {
  white-space: nowrap;
}

.faq-list {
  border-top: 1px solid var(--rule-strong);
}

.faq {
  border-bottom: 1px solid var(--rule-strong);
}

.faq-q {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 22px 0;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-family: var(--serif);
  font-size: 1.3rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.faq-sign {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid var(--rule-strong);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.faq.open .faq-sign {
  background: var(--green);
  border-color: var(--green);
  color: #fff;
}

.faq-a {
  max-width: 640px;
  padding: 0 0 24px;
  font-size: 1rem;
  line-height: 1.7;
  color: var(--ink-2);
}

/* ==================== FINAL CTA ==================== */
.final-cta {
  position: relative;
  overflow: hidden;
  padding: 120px 0;
  background: var(--green);
  color: #fff;
}

.cta-seal {
  position: absolute;
  right: -90px;
  top: 50%;
  transform: translateY(-50%) rotate(-8deg);
  opacity: 0.1;
  pointer-events: none;
}

.cta-inner {
  position: relative;
}

.mc-site .cta-title {
  max-width: 760px;
  font-size: clamp(2.3rem, 4.8vw, 3.5rem);
  line-height: 1.08;
  color: #fff;
  margin-bottom: 18px;
  text-wrap: balance;
}

.cta-sub {
  max-width: 600px;
  margin-bottom: 34px;
  font-size: 1.12rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
}

.cta-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 28px;
}

.mc-site .cta-phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1.5px solid rgba(255, 255, 255, 0.4);
  padding-bottom: 2px;
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1100px) {
  .module-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .tiers,
  .sec-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .sec-item:not(:nth-child(4n + 1)) {
    padding-left: 0;
    border-left: 0;
  }
  .sec-item:nth-child(2n) {
    padding-left: 24px;
    border-left: 1px solid rgba(243, 237, 224, 0.1);
  }
  .apps {
    grid-template-columns: 1fr 1fr;
  }
  .apps .app-card:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 980px) {
  .hero {
    padding: 48px 0 80px;
  }
  .hero-inner,
  .modules-head,
  .security-head,
  .fees-layout,
  .faq-layout {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .hero-inner {
    gap: 56px;
  }
  .hero-visual {
    max-width: 540px;
    width: 100%;
    margin: 0 auto;
  }
  .aud-grid,
  .pricing-foot {
    grid-template-columns: 1fr;
  }
  .fees-layout {
    gap: 48px;
  }
}

@media (max-width: 700px) {
  .apps {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .hero-ledger::before {
    display: none;
  }
  .hero-ctas .mc-btn {
    flex: 1;
  }
  .figure {
    min-width: 0;
    padding-right: 12px;
  }
  .figure + .figure {
    padding-left: 12px;
  }
  .figure dd {
    font-size: 1.55rem;
  }
  .hero-visual {
    height: 560px;
  }
  .slip {
    width: 210px;
    top: 90px;
    left: -6px;
    padding: 18px 16px 16px;
  }
  .phone {
    right: -4px;
    width: 250px;
    height: 520px;
  }
  .ph-tile-icon {
    width: 36px;
    height: 36px;
  }
  .posted {
    left: 0;
    bottom: 10px;
  }
  .module-grid,
  .tiers,
  .sec-grid,
  .included ul,
  .aud-points {
    grid-template-columns: 1fr;
  }
  .sec-item:nth-child(2n) {
    padding-left: 0;
    border-left: 0;
  }
  .module {
    padding: 26px 22px 28px;
  }
  .aud {
    padding: 24px 22px 26px;
  }
  .receipt {
    padding: 24px 20px 34px;
  }
  .receipt-stamp {
    display: none;
  }
  .fees-list li {
    flex-direction: column;
    gap: 2px;
  }
  .fees-list b {
    text-align: left;
  }
  .r-line {
    font-size: 0.86rem;
  }
  .slip {
    left: 0;
  }
  .faq-q {
    font-size: 1.12rem;
  }
  .final-cta {
    padding: 88px 0;
  }
  .cta-actions .mc-btn {
    width: 100%;
  }
}
</style>
